import { clsx } from "clsx";
import { Folder } from "lucide-react";
import { SubventionFeedRow } from "@/features/subventions/components/feed/SubventionFeedRow";
import type { EligibleSubsidy } from "@/features/subventions/types/subvention.types";
import type { EnvNode, FeedNode } from "@/features/subventions/utils/buildFeedTree";
import { sortNodes } from "@/features/subventions/utils/buildFeedTree";
import {
  earliestPeriodCap, eur, type StatusTone, stripLeadingEmoji,
} from "@/features/subventions/utils/subventionStatus";

type SubventionEnvelopeProps = {
  node: EnvNode
  depth: number
  onOpenDetail: (subsidy: EligibleSubsidy) => void
};

type EnvBudget = {
  remaining: number
  cap: { value: number, periodFr: string } | null
  pct: number
  tone: StatusTone
};

// Feed gauges are numbers, never bars: the shared budget is a toned chip, coloured by consumption.
const CHIP_TONE: Record<StatusTone, string> = {
  ok: "bg-lightsuccess text-success",
  warn: "bg-lightwarning text-warning-emphasis",
  depleted: "bg-lighterror text-error-emphasis",
};

// The envelope's shared budget: a remaining figure, plus a fill when the parent also carries a
// period cap (rare). A budget-less envelope is a pure structural grouping (no chip, no bar).
const envBudget = (node: EnvNode): EnvBudget | null => {
  const remaining = node.first?.global_remaining_amount;
  if (!node.hasBudget || remaining === null || remaining === undefined) return null;
  const cap = earliestPeriodCap(node.first?.global_limits?.amount);
  if (!cap) return {
    remaining, cap: null, pct: 0, tone: "ok",
  };
  const used = Math.min(Math.max(cap.value - remaining, 0), cap.value);
  const pct = cap.value > 0 ? Math.round((used / cap.value) * 100) : 0;
  const tone: StatusTone = pct >= 100 ? "depleted" : pct >= 75 ? "warn" : "ok";
  return {
    remaining, cap, pct, tone,
  };
};

const renderChild = (
  child: FeedNode,
  depth: number,
  onOpenDetail: (subsidy: EligibleSubsidy) => void,
) => (child.type === "env"
  ? (
    <SubventionEnvelope
      key={`env-${child.id}`}
      node={child}
      depth={depth + 1}
      onOpenDetail={onOpenDetail}
    />
  )
  : (
    <SubventionFeedRow
      key={`${child.subsidy.subsidy_perimeter_id}-${child.subsidy.subsidy_perimeter_name}`}
      subsidy={child.subsidy}
      onOpenDetail={onOpenDetail}
      insideEnv
    />
  ));

export const SubventionEnvelope = ({ node, depth, onOpenDetail }: SubventionEnvelopeProps) => {
  const budget = envBudget(node);
  const subline = budget ? "Budget partagé entre les avantages ci-dessous" : "Avantages regroupés";
  // The envelope always wears the folder tile; its name shows with any leading icons stripped.
  const label = stripLeadingEmoji(node.env);

  return (
    <section
      aria-label={label}
      className={clsx(
        "rounded-2xl border border-primary/10 bg-lightprimary/40 dark:border-primary/20 dark:bg-primary/5",
        depth > 0 && "border-l-[3px] border-l-primary",
      )}
    >
      <div className="flex items-center gap-3 px-4 pb-2 pt-3.5">
        <span className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-xl bg-white text-gray-400 dark:bg-gray-700 dark:text-gray-400">
          <Folder size={22} aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className={clsx("block font-bold leading-tight text-gray-900 dark:text-white", depth > 0 ? "text-sm" : "text-[15px]")}>
            {label}
          </span>
          <span className="mt-0.5 block text-[11px] text-gray-400 dark:text-gray-500">{subline}</span>
        </span>
        {budget && (
          <span className={clsx("inline-flex shrink-0 items-baseline gap-1 rounded-full px-2.5 py-1 text-[12.5px] font-extrabold tabular-nums", CHIP_TONE[budget.tone])}>
            {eur(budget.remaining)}
            {budget.cap && (
              <span className="text-[10.5px] font-semibold opacity-80">
                {`/ ${eur(budget.cap.value)}${budget.cap.periodFr ? ` ${budget.cap.periodFr}` : ""}`}
              </span>
            )}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2.5 px-3 pb-3 pl-5">
        {sortNodes(node.children).map((child) => renderChild(child, depth, onOpenDetail))}
      </div>
    </section>
  );
};

SubventionEnvelope.displayName = "SubventionEnvelope";
